import { BlockMetadata, PipelinePhase } from '@visualizer/types';
import { BlockParameters, PipelineBlock } from './PipelineBlock';

export enum BlockKeys {
    Threshold = 'threshold',
    Slice = 'slice',
    Unobstructed = 'unobstructed',
    NoBase = 'nobase',
    Alone = 'alone',
    Map = 'map',
    Averages = 'averages',
    Invert = 'invert',
    CloseToProjSketch = 'closeToProjectSketch',
    Projection = 'projection',
    CloseToTerrain = 'closeToTerrain',
    Density = 'density',
    InsidePolygon = 'insidePolygon',
    Shadow = 'shadow',
    SolarParameters = 'solarParams',
    HideZeroValues = 'hideZeroValues',
    RemoveSensorsById = 'removeSensorsById',
    CloseToDesignSchemes = 'closeToDesignSchemes',
    PvPotentials = 'pvPotentials',
    ShgPotentials = 'shgPotentials',
}
/**
 * Block constructor type
 */
type BlockConstructor<T extends BlockParameters = BlockParameters> = {
    new (params: T, id?: string): PipelineBlock<T>;
    metadata: BlockMetadata;
};

/**
 * Registry and factory for pipeline blocks
 */
export class BlockCatalog {
    private static readonly blockRegistry = new Map<string, BlockConstructor>();

    /**
     * Register a block class
     */
    static registerBlock<T extends BlockParameters>(BlockClass: BlockConstructor<T>): void {
        const metadata = BlockClass.metadata;

        if (!metadata) {
            throw new Error(`Block "${BlockClass.name}" is missing metadata`);
        }
        const type = metadata.type;
        this.blockRegistry.set(type, BlockClass as unknown as BlockConstructor);
    }

    /**
     * Create a block instance
     */
    static createBlock<T extends BlockParameters>(type: string, params: T, id?: string): PipelineBlock<T> {
        const BlockClass = this.blockRegistry.get(type) as BlockConstructor<T> | undefined;

        if (!BlockClass) {
            throw new Error(`Block type "${type}" not found in registry`);
        }

        return new BlockClass(params, id);
    }

    /**
     * Get metadata for a block type
     */
    static getBlockMetadata(type: string): BlockMetadata | undefined {
        const BlockClass = this.blockRegistry.get(type);
        return BlockClass?.metadata;
    }

    static resolvePipelinePhase(metadata?: BlockMetadata): PipelinePhase {
        return metadata?.pipelinePhase ?? PipelinePhase.Regular;
    }

    static getPipelinePhase(type: string): PipelinePhase {
        return this.resolvePipelinePhase(this.getBlockMetadata(type));
    }

    /**
     * Check if a block type is unique
     */
    static isUnique(type: string): boolean {
        const meta = this.getBlockMetadata(type) as (BlockMetadata & { unique?: boolean }) | undefined;
        return !!meta?.unique;
    }

    /**
     * Get all registered block types
     */
    static getCollection(): BlockMetadata[] {
        return Array.from(this.blockRegistry.values()).map((BlockClass) => BlockClass.metadata);
    }

    /**
     * Check if a block type is registered
     */
    static hasBlock(type: string): boolean {
        return this.blockRegistry.has(type);
    }

    static getVisibleCollection(): BlockMetadata[] {
        return this.getCollection().filter((b) => b.visible !== false && this.resolvePipelinePhase(b) === PipelinePhase.Regular);
    }

    /**
     * Check if a block type belongs to the regular-pipeline phase.
     */
    static isRegularBlock(type: string): boolean {
        return this.getPipelinePhase(type) === PipelinePhase.Regular;
    }
}

/**
 * Decorator to register a block class
 */
export function AddMetadata(metadata: BlockMetadata) {
    return function <T extends { new (...args: any[]): PipelineBlock<any> }>(constructor: T) {
        // Attach metadata to the constructor
        if (metadata) (constructor as any).metadata = metadata;

        BlockCatalog.registerBlock(constructor as any);
        return constructor;
    };
}
